'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { Icon, IconComponent } from '@graftcode/design-system'

import { CHALLENGES } from '@/modules/code-sandbox/challenges/challenges.data'
import { LANGUAGE_CONFIGS } from '@/modules/code-sandbox/challenges/challenges.types'
import { CODE_SANDBOX_MENU_TEST_IDS } from '@/shared/academy-test-ids.const'

import styles from './CodeSandboxMenu.module.scss'

const DIFFICULTY_ORDER = { beginner: 0, intermediate: 1, advanced: 2 } as const

export function CodeSandboxMenu() {
  const pathname = usePathname()

  const sortedChallenges = [...CHALLENGES].sort(
    (a, b) => DIFFICULTY_ORDER[a.difficulty] - DIFFICULTY_ORDER[b.difficulty]
  )

  return (
    <div className={styles.menu} data-testid={CODE_SANDBOX_MENU_TEST_IDS.root}>
      <Link href="/" className={styles.backButton}>
        <IconComponent icon={Icon.ArrowLeftRegular} size={16} />
        <span>Back to Home</span>
      </Link>

      <div className={styles.header}>
        <Link href="/code-sandbox" className={styles.titleLink}>
          <h3 className={styles.title}>Code Sandbox</h3>
        </Link>
        <p className={styles.subtitle}>Pick a challenge to start coding</p>
      </div>

      <ul className={styles.list}>
        {sortedChallenges.map((challenge) => {
          const challengePath = `/code-sandbox/${challenge.id}`
          const isActive = pathname === challengePath

          return (
            <li key={challenge.id} className={`${styles.item} ${isActive ? styles.active : ''}`}>
              <Link href={challengePath} className={styles.link} aria-current={isActive ? 'page' : undefined}>
                <div className={styles.challengeInfo}>
                  <span className={styles.challengeTitle}>{challenge.title}</span>
                  <span className={styles.challengeMeta}>
                    <span className={`${styles.difficulty} ${styles[challenge.difficulty]}`}>
                      {challenge.difficulty}
                    </span>
                    <span className={styles.separator}>·</span>
                    <span className={styles.time}>{challenge.estimatedMinutes} min</span>
                  </span>
                </div>
                <div className={styles.languages}>
                  {challenge.supportedLanguages.slice(0, 4).map((lang) => (
                    <span key={lang} className={styles.langTag} title={LANGUAGE_CONFIGS[lang].label}>
                      <IconComponent icon={LANGUAGE_CONFIGS[lang].icon} size={16} />
                    </span>
                  ))}
                  {challenge.supportedLanguages.length > 4 && (
                    <span className={styles.langTag}>+{challenge.supportedLanguages.length - 4}</span>
                  )}
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
